import { OPERATIONS_MODULES, OPS_ACCENT, type OperationsModule } from './operationsModules'
import { StatusBadge } from './OperationsMiniPortal'
import { CommandCenterShell } from '../shell'
import './operations-surface.css'

/**
 * OperationsHome — the /square/command-center/operations command deck.
 *
 * PHASE_2G: renders inside the shared CommandCenterShell (same header / Q home /
 * back / breadcrumb / honesty badge / wide frame as every other V2 surface).
 * CHAT #3 conversion: the card grid content root adopts the shared
 * `.sqv-surface` foundation (canonical #0b0d17) and all presentation moved to
 * operations-surface.css. The only inline style is the DYNAMIC per-card accent
 * (--sqv-accent). Behavior is unchanged: each card opens an INTERNAL V2 mini
 * portal (/operations/<id>) — never another domain.
 */
export interface OperationsHomeProps {
  /** Open a module's internal mini portal (its `path`). */
  onOpenModule?: (path: string) => void
  /** Back to the V2 command center (/square/command-center). */
  onBackCommandCenter?: () => void
  /** Q home — the main Square dashboard (/square/dashboard). */
  onHome?: () => void
}

function ModuleCard({ module, onOpenModule }: { module: OperationsModule; onOpenModule?: (path: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpenModule?.(module.path)}
      className="sqv-card sqv-ops-card"
      data-interactive={true}
      data-status={module.status}
      style={{ ['--sqv-accent']: module.accent } as React.CSSProperties}
    >
      <div className="sqv-ops-card__head">
        <strong className="sqv-ops-card__title">{module.label}</strong>
        <StatusBadge status={module.status} size="sm" />
      </div>
      <p className="sqv-ops-card__purpose">{module.purpose}</p>
      <span className="sqv-ops-card__open">Open portal →</span>
    </button>
  )
}

export default function OperationsHome({ onOpenModule, onBackCommandCenter, onHome }: OperationsHomeProps) {
  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      breadcrumb={['COMMAND CENTER', 'OPERATIONS']}
      title="Operations"
      titleAccent={OPS_ACCENT}
      honestyBadge={{
        label: 'INTERNAL PORTALS · NOT FAKED',
        title: 'Every card opens an internal Square Code mini portal — never another domain. Status badges are honest: OPEN/READY portals are usable now; PLANNED / SETUP-REQUIRED / DEFERRED portals say so plainly.',
      }}
    >
      <div className="sqv-surface sqv-ops sqv-ops-home">
        <p className="sqv-ops-home__intro">
          Discovery, intake, jobs, workflows, boards, project setup, onboarding, runs, and daily meetings — each opens a workable
          internal portal in this same command-center flow.
        </p>
        <div className="sqv-ops-home__grid">
          {OPERATIONS_MODULES.map((m) => <ModuleCard key={m.id} module={m} onOpenModule={onOpenModule} />)}
        </div>
      </div>
    </CommandCenterShell>
  )
}
